import React from 'react';
import { motion } from 'framer-motion';
import { TargetIcon, ShieldCheckIcon, RocketIcon } from 'lucide-react';
export function AnimatedPipelineEngine({
  className = ''
}: {
  className?: string;
}) {
  const stages = [{
    icon: TargetIcon,
    title: 'Recruiting',
    subtitle: 'Sourcing'
  }, {
    icon: ShieldCheckIcon,
    title: 'Retention',
    subtitle: 'Anchoring'
  }, {
    icon: RocketIcon,
    title: 'Results',
    subtitle: 'Production'
  }];
  return <div className={`relative ${className}`} data-id="element-1087">
      {/* CSS Animations */}
      <style data-id="element-1088">{`
        @keyframes flowParticle1 {
          0% { left: 0%; opacity: 0; }
          5% { opacity: 1; }
          45% { opacity: 1; }
          50% { left: 50%; opacity: 0; }
          100% { left: 50%; opacity: 0; }
        }
        @keyframes flowParticle2 {
          0% { left: 50%; opacity: 0; }
          5% { opacity: 1; }
          45% { opacity: 1; }
          50% { left: 100%; opacity: 0; }
          100% { left: 100%; opacity: 0; }
        }
        @keyframes pulseRing {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.15); opacity: 0.3; }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px 5px rgba(127, 224, 195, 0.3); }
          50% { box-shadow: 0 0 40px 15px rgba(127, 224, 195, 0.5); }
        }
        @keyframes particleTrail {
          0% { transform: translateX(-100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        .particle-flow-1 {
          animation: flowParticle1 4s ease-in-out infinite;
        }
        .particle-flow-2 {
          animation: flowParticle2 4s ease-in-out infinite;
          animation-delay: 2s;
        }
        .particle-flow-3 {
          animation: flowParticle1 4s ease-in-out infinite;
          animation-delay: 1s;
        }
        .particle-flow-4 {
          animation: flowParticle2 4s ease-in-out infinite;
          animation-delay: 3s;
        }
        .pulse-ring {
          animation: pulseRing 3s ease-in-out infinite;
        }
        .pulse-ring-delay-1 {
          animation: pulseRing 3s ease-in-out infinite;
          animation-delay: 1s;
        }
        .pulse-ring-delay-2 {
          animation: pulseRing 3s ease-in-out infinite;
          animation-delay: 2s;
        }
        .node-glow {
          animation: pulseGlow 4s ease-in-out infinite;
        }
        .node-glow-delay-1 {
          animation: pulseGlow 4s ease-in-out infinite;
          animation-delay: 1.3s;
        }
        .node-glow-delay-2 {
          animation: pulseGlow 4s ease-in-out infinite;
          animation-delay: 2.6s;
        }
      `}</style>

      {/* Desktop Layout */}
      <div className="hidden md:block" data-id="element-1089">
        <div className="relative max-w-4xl mx-auto" data-id="element-1090">
          {/* Connection Lines */}
          <div className="absolute top-1/2 left-[16.67%] right-[16.67%] h-1 -translate-y-1/2" data-id="element-1091">
            {/* Line Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-mint/20 via-mint/40 to-mint/20 rounded-full" data-id="element-1092" />

            {/* Animated Particles on Line 1 */}
            <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-mint rounded-full blur-[2px] particle-flow-1" style={{
            left: '0%'
          }} data-id="element-1093" />
            <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-mint rounded-full blur-[1px] particle-flow-3" style={{
            left: '0%'
          }} data-id="element-1094" />

            {/* Animated Particles on Line 2 */}
            <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-mint rounded-full blur-[2px] particle-flow-2" style={{
            left: '50%'
          }} data-id="element-1095" />
            <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-mint rounded-full blur-[1px] particle-flow-4" style={{
            left: '50%'
          }} data-id="element-1096" />
          </div>

          {/* Stage Nodes */}
          <div className="relative flex justify-between items-center" data-id="element-1097">
            {stages.map((stage, index) => {
            const Icon = stage.icon;
            const glowClass = index === 0 ? 'node-glow' : index === 1 ? 'node-glow-delay-1' : 'node-glow-delay-2';
            const ringClass = index === 0 ? 'pulse-ring' : index === 1 ? 'pulse-ring-delay-1' : 'pulse-ring-delay-2';
            return <motion.div key={index} className="relative flex flex-col items-center" initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.2,
              duration: 0.5
            }} data-id="element-1098">
                  {/* Outer Pulse Ring */}
                  <div className={`absolute w-28 h-28 rounded-full border-2 border-mint/30 ${ringClass}`} data-id="element-1099" />

                  {/* Node */}
                  <div className={`relative w-20 h-20 rounded-full bg-deep-blue border-2 border-mint flex items-center justify-center ${glowClass}`} data-id="element-1100">
                    <Icon className="w-8 h-8 text-mint" data-id="element-1101" />
                  </div>

                  {/* Labels */}
                  <div className="mt-4 text-center" data-id="element-1102">
                    <div className="text-xs font-semibold text-mint uppercase tracking-wider mb-1" data-id="element-1103">
                      {stage.subtitle}
                    </div>
                    <div className="text-lg font-bold text-white" data-id="element-1104">
                      {stage.title}
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Arrow Indicators */}
          <div className="absolute top-1/2 left-[38%] -translate-y-1/2 -translate-x-1/2" data-id="element-1105">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-mint/60" data-id="element-1106">
              <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1107" />
            </svg>
          </div>
          <div className="absolute top-1/2 left-[62%] -translate-y-1/2 -translate-x-1/2" data-id="element-1108">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-mint/60" data-id="element-1109">
              <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1110" />
            </svg>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Vertical */}
      <div className="md:hidden" data-id="element-1111">
        <div className="relative max-w-xs mx-auto" data-id="element-1112">
          {/* Vertical Connection Line */}
          <div className="absolute left-1/2 top-[60px] bottom-[60px] w-1 -translate-x-1/2" data-id="element-1113">
            <div className="absolute inset-0 bg-gradient-to-b from-mint/20 via-mint/40 to-mint/20 rounded-full" data-id="element-1114" />
          </div>

          {/* Stage Nodes */}
          <div className="relative flex flex-col items-center space-y-12" data-id="element-1115">
            {stages.map((stage, index) => {
            const Icon = stage.icon;
            const glowClass = index === 0 ? 'node-glow' : index === 1 ? 'node-glow-delay-1' : 'node-glow-delay-2';
            return <motion.div key={index} className="relative flex flex-col items-center z-10" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15,
              duration: 0.4
            }} data-id="element-1116">
                  {/* Node */}
                  <div className={`relative w-16 h-16 rounded-full bg-deep-blue border-2 border-mint flex items-center justify-center ${glowClass}`} data-id="element-1117">
                    <Icon className="w-7 h-7 text-mint" data-id="element-1118" />
                  </div>

                  {/* Labels */}
                  <div className="mt-3 text-center" data-id="element-1119">
                    <div className="text-xs font-semibold text-mint uppercase tracking-wider mb-1" data-id="element-1120">
                      {stage.subtitle}
                    </div>
                    <div className="text-base font-bold text-white" data-id="element-1121">
                      {stage.title}
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}